import React from "react";
import { IconCheck, IconAlertTriangle } from "@tabler/icons";
import { Text, Title, Paper, Group, Stack, Box, Badge } from "@mantine/core";

const EquationTerm = ({ label, amount, count, formatter, highlight }) => (
  <Box sx={{ textAlign: "left", minWidth: 120 }}>
    <Group spacing={6} mb={4} noWrap>
      <Text size="xs" color="dimmed">
        {label}
      </Text>
      {count != null && (
        <Badge size="xs" variant="outline" color="gray">
          {count}
        </Badge>
      )}
    </Group>
    <Text
      size="md"
      weight={highlight ? 700 : 600}
      color={highlight ? "teal" : amount >= 0 ? "green" : "red"}
    >
      {formatter.currency(amount)}
    </Text>
  </Box>
);

const Operator = ({ symbol }) => (
  <Text
    size="lg"
    weight={700}
    color="dimmed"
    sx={{ alignSelf: "flex-end", pb: 4 }}
  >
    {symbol}
  </Text>
);

const SectionHeader = ({ title, subtitle }) => (
  <Stack spacing={4} mb="sm">
    <Title order={5}>{title}</Title>
    <Text size="xs" color="dimmed">
      {subtitle}
    </Text>
  </Stack>
);

const PayrollBidsVerification = ({ verification, formatter }) => {
  if (!verification) return null;

  const {
    fromClientPayments,
    fromCommissionStatement,
    adjustments,
    orderCommission,
    incentiveCharge,
    releasedFromHoldingPool,
    fromClientPaymentsCount,
    fromCommissionStatementCount,
    adjustmentsCount,
    orderCommissionCount,
    incentiveChargeCount,
    releasedBidCount,
    totalBidsCount,
    totalBidsAmount,
    row2Balanced,
  } = verification;

  const showAdjustments = Math.abs(adjustments || 0) >= 0.01;
  const row2Diff = Math.abs(
    (orderCommission || 0) +
      (incentiveCharge || 0) +
      (releasedFromHoldingPool || 0) -
      (totalBidsAmount || 0),
  );

  return (
    <Stack spacing="sm" mb="md">
      <Paper p="md" withBorder>
        <SectionHeader
          title="Order Commission Breakdown"
          subtitle="How commission bids from client payments and commission statements compose the order commission total"
        />
        <Group position="left" spacing="md" wrap="wrap">
          <EquationTerm
            label="From Client Payments"
            amount={fromClientPayments}
            count={fromClientPaymentsCount}
            formatter={formatter}
          />
          <Operator symbol="+" />
          <EquationTerm
            label="From Commission Statement"
            amount={fromCommissionStatement}
            count={fromCommissionStatementCount}
            formatter={formatter}
          />
          {showAdjustments && (
            <>
              <Operator symbol="+" />
              <EquationTerm
                label="Payment Adjustment"
                amount={adjustments}
                count={adjustmentsCount}
                formatter={formatter}
              />
            </>
          )}
          <Operator symbol="=" />
          <EquationTerm
            label="Order Commission"
            amount={orderCommission}
            count={orderCommissionCount}
            formatter={formatter}
            highlight
          />
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <SectionHeader
          title="Total Bids Verification"
          subtitle="How order commission, incentives/charges, and bids released from the holding pool sum to the total bids amount"
        />
        <Group position="apart" align="flex-start" wrap="nowrap">
          <Group position="left" spacing="md" wrap="wrap" sx={{ flex: 1 }}>
            <EquationTerm
              label="Order Commission"
              amount={orderCommission}
              count={orderCommissionCount}
              formatter={formatter}
            />
            <Operator symbol="+" />
            <EquationTerm
              label="Incentive / Charge"
              amount={incentiveCharge}
              count={incentiveChargeCount}
              formatter={formatter}
            />
            <Operator symbol="+" />
            <EquationTerm
              label="Released from Holding Pool"
              amount={releasedFromHoldingPool}
              count={releasedBidCount}
              formatter={formatter}
            />
            <Operator symbol="=" />
            <EquationTerm
              label="Total Bids Amount"
              amount={totalBidsAmount}
              count={totalBidsCount}
              formatter={formatter}
              highlight
            />
          </Group>
          <Group spacing={4} noWrap sx={{ alignSelf: "center" }}>
            {row2Balanced ? (
              <>
                <IconCheck size={16} color="#40c057" />
                <Text size="xs" color="green">
                  Balanced
                </Text>
              </>
            ) : (
              <>
                <IconAlertTriangle size={16} color="#fd7e14" />
                <Text size="xs" color="orange">
                  Mismatch ({formatter.currency(row2Diff)})
                </Text>
              </>
            )}
          </Group>
        </Group>
      </Paper>
    </Stack>
  );
};

export default PayrollBidsVerification;
